import { useEffect, useMemo } from 'react';
import { UseDataFetchingProps, ApiResponse } from '../types/index';
import { CACHE_DURATION, MAX_CACHE_ENTRIES, FETCH_DEBOUNCE_DELAY } from '../constants/index';
import buildResultEndpoint from '../../../utils/buildResultEndpoint';
import fetchEndpoint from '../../../services/fetchEndpoint';

export const useDataFetching = ({
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  skip,
  limit,
  orderByParam,
  order,
  defaultFilterValue,
  sourceWeights,
  userContext,
  mappingContent,
  checkIncompleteFilters,
  setRowData,
  setTotalCount,
  setTotalResults,
  setLoading,
  setIsSessionExpired,
  setShowPopup,
  setIncompleteFilterMessage,
  cacheRef,
  prevDataRef,
  fetchTimeoutRef,
}: UseDataFetchingProps) => {
  const {
    isAuthenticated,
    authorization,
    setAuthorization,
    setIsAuthenticated,
    setGroups,
    setAuthError,
    restrictGridResultCount,
    restrictBrowseAll,
    groups,
  } = userContext;

  const isTrial = groups?.includes('ROLE_TRIAL');

  const filterValuesKey = useMemo(
    () =>
      JSON.stringify(
        defaultFilterValue
          .filter(f => f.value)
          .map(f => ({ name: f.name, value: f.value, operator: f.operator })),
      ),
    [defaultFilterValue],
  );

  useEffect(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    fetchTimeoutRef.current = setTimeout(() => {
      if (!(view && source && tab && searchItems)) {
        return;
      }

      const incompleteMessage = checkIncompleteFilters(defaultFilterValue);
      setIncompleteFilterMessage(incompleteMessage);

      if (incompleteMessage) {
        setRowData([]);
        setTotalCount(0);
        setTotalResults(0);
        prevDataRef.current = { data: [], count: 0 };
        return;
      }

      let urlEndpoint = buildResultEndpoint({
        view,
        source,
        tab,
        searchItems,
        isEvidence,
        isMapping,
        mappingContent,
        pageNum: restrictGridResultCount || (restrictBrowseAll && view === 'ALL') ? 0 : skip / limit,
        pageSize: limit,
        orderByParam,
        order,
        defaultFilterValue: defaultFilterValue as any,
        sourceWeights,
      });

      if (!isAuthenticated && skip !== 0) {
        setShowPopup(true);
        return;
      }

      if (restrictBrowseAll && view === 'ALL' && skip !== 0) {
        setShowPopup(true);
        return;
      }

      if (isTrial && skip !== 0) {
        setShowPopup(true);
        return;
      }

      if (!urlEndpoint) return;
      urlEndpoint = !isAuthenticated ? `${urlEndpoint}&skey=unauth` : urlEndpoint;

      // Check cache first
      const cacheKey = urlEndpoint;
      if (cacheRef.current[cacheKey]) {
        const cached = cacheRef.current[cacheKey];
        const isRecentCache = Date.now() - cached.timestamp < CACHE_DURATION;

        if (isRecentCache) {
          setRowData(cached.data);
          setTotalCount(cached.totalCount);
          setTotalResults(cached.totalCount);
          setIsSessionExpired(false);
          prevDataRef.current = { data: cached.data, count: cached.totalCount };
          return;
        } else {
          // Remove stale cache
          delete cacheRef.current[cacheKey];
        }
      }

      setLoading(true);
      (fetchEndpoint as any)(
        urlEndpoint,
        authorization,
        setAuthorization,
        setIsAuthenticated,
        setGroups,
        setAuthError,
      )
        .then((res: ApiResponse) => {
          const data = res?.payload;
          const totalElements = res?.paging ? res.paging.totalElements : 0;

          if (data) {
            let totalCountValue = parseInt(totalElements.toString());

            const hasSpecificSearchItems =
              searchItems && searchItems.length > 0 && !searchItems.some((item: any) => item.id === 'ALL');

            const urlParams = new URLSearchParams(window.location.search);
            const identsParam = urlParams.get('idents');
            const isFromAdvancedSearch = identsParam && identsParam !== 'ALL' && identsParam.includes('-');

            if (hasSpecificSearchItems || isFromAdvancedSearch) {
              const filteredCount =
                res?.paging?.filteredElements || res?.paging?.filteredCount || res?.filteredCount;

              if (filteredCount && filteredCount !== totalCountValue && filteredCount < totalCountValue) {
                totalCountValue = parseInt(filteredCount.toString());
              } else {
                if (data.length === 0) {
                  totalCountValue = 0;
                } else if (skip === 0 && data.length < limit) {
                  totalCountValue = data.length;
                } else {
                  const isUnreasonablyHigh = totalCountValue > 100000;

                  if (isUnreasonablyHigh) {
                    if (data.length === limit) {
                      totalCountValue = Math.min(data.length * 50, 10000);
                    } else {
                      totalCountValue = skip + data.length;
                    }
                  }
                }
              }
            }

            setRowData(data);
            setTotalCount(totalCountValue);
            setTotalResults(totalCountValue);
            setIsSessionExpired(false);

            prevDataRef.current = { data, count: totalCountValue };

            // Cache the response
            cacheRef.current[cacheKey] = {
              data: data,
              totalCount: totalCountValue,
              timestamp: Date.now(),
            };

            // Clean old cache entries
            const cacheKeys = Object.keys(cacheRef.current);
            if (cacheKeys.length > MAX_CACHE_ENTRIES) {
              const sortedKeys = cacheKeys.sort(
                (a, b) => cacheRef.current[a].timestamp - cacheRef.current[b].timestamp,
              );
              sortedKeys.slice(0, cacheKeys.length - MAX_CACHE_ENTRIES).forEach(key => {
                delete cacheRef.current[key];
              });
            }
          } else if (res?.status === 403) {
            setIsSessionExpired(true);
            setRowData([]);
            setTotalCount(0);
            prevDataRef.current = { data: [], count: 0 };
          } else {
            setRowData([]);
            setTotalCount(0);
            prevDataRef.current = { data: [], count: 0 };
          }
        })
        .catch(() => {
          setRowData([]);
          setTotalCount(0);
        })
        .finally(() => {
          setLoading(false);
        });
    }, FETCH_DEBOUNCE_DELAY);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [
    view,
    source,
    tab,
    searchItems,
    skip,
    limit,
    order,
    orderByParam,
    filterValuesKey,
    sourceWeights,
    isEvidence,
    isMapping,
    isAuthenticated,
    mappingContent,
    checkIncompleteFilters,
    setRowData,
    setTotalCount,
    setTotalResults,
    setLoading,
    setIsSessionExpired,
    setShowPopup,
    setIncompleteFilterMessage,
    cacheRef,
    prevDataRef,
    fetchTimeoutRef,
    authorization,
    setAuthorization,
    setIsAuthenticated,
    setGroups,
    setAuthError,
    restrictGridResultCount,
    restrictBrowseAll,
    isTrial,
  ]);

  return {
    filterValuesKey,
  };
};